import { useEffect, useState } from "react";
import { msg } from "./i18n";
/**
 * Neuerungen (nur Logiful intern): wesentliche Änderungen und Funktionen zum Nachlesen für das Logiful-Team.
 * Bei jeder nennenswerten Änderung oben einen Eintrag ergänzen (neueste zuerst). Interne Hinweise dürfen hier
 * stehen (die Seite sehen nur Logiful-Mitarbeiter) – Kunden informiert die Hilfe bzw. „News & Termine“.
 */
export type ChangeKind = "new" | "improved" | "fixed";

export interface ChangelogEntry {
  /** eindeutig und stabil (für „neu seit dem letzten Besuch“) */
  id: string;
  /** YYYY-MM-DD (Live-Datum) */
  date: string;
  kind: ChangeKind;
  area: string;
  title: string;
  /** was sich für Kunden bzw. im Portal ändert */
  summary: string;
  /** Einzelheiten in Stichpunkten */
  details?: string[];
  /** nur für das Logiful-Team: wie einschalten, worauf achten, was nicht nach außen geht */
  internal?: string;
  /** Kunden sehen die Änderung (sonst nur Logiful-intern) */
  customerVisible: boolean;
  /** Seite im Portal */
  to?: string;
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    id: "2026-10-30-dhl-tracking-link",
    date: "2026-10-30",
    kind: "new",
    area: "Shops",
    title: "Fester DHL-Tracking-Link je Shop",
    summary: "Je Shopify-Shop einstellbar: DHL- und Warenpost-Sendungen gehen immer mit dem Link zur DHL-Sendungsverfolgung auf dhl.de an Shopify – unabhängig davon, wie Shopify den Dienstleister zuordnet.",
    details: [
      "Shop → Synchronisation → Aufträge → „Tracking-Link in Shopify“",
      "Andere Dienstleister (DPD, DHL Express …) bleiben unverändert",
      "Gilt für Sendungen, die ab dem Umschalten gemeldet werden",
    ],
    internal: "channels.config.trackingLink = dhl_de → applyChannelTrackingLink (packages/db/tracking.ts) in reportShipmentNow: carrier „DHL Paket“, carrierExact, piececode-Link. Eigene Tracking-Seite (linkInShop) hat weiter Vorrang. Für MTN per Datenkorrektur eingeschaltet.",
    customerVisible: true,
  },
  {
    id: "2026-10-26-recall-stock-sync",
    date: "2026-10-26",
    kind: "new",
    area: "Aufträge",
    title: "Aus dem Lager zurückholen – und Bestand ohne Verzögerung",
    summary: "Übertragene Aufträge lassen sich zurück ins Portal holen, um sie zu bearbeiten oder teilzuliefern – mit ihrem Platz und Bestand. Außerdem führt das Portal den Bestand bei Übertragung und Storno sofort mit, statt bis zum nächsten Abgleich mit dem Lager zu warten.",
    details: [
      "Menü im Auftrag → „Aus dem Lager zurückholen“ (live geprüft, bis zur Kommissionierung); „Freigeben“ überträgt ihn wieder",
      "Anders als „stornieren und neu anfragen“ geht der frei werdende Bestand nicht an andere Aufträge",
      "Bestand: übertragene Ware wird sofort abgezogen, im Lager stornierte sofort zurückgebucht – keine Doppelvergabe mehr",
      "Verfügbarkeit im Auftrag unterscheidet „verfügbar“ von „im Lager, schon für übertragene Aufträge reserviert“",
    ],
    internal: "adjustStockForOrder (packages/db/stock-adjust.ts) bei Übergabe (−), Freigabe geparkter (−), Storno im Lager (+, Worker und API, nicht bei geparkten); der FFN-Abgleich überschreibt mit echten Zahlen. orders.recalled_at, Haltegrund RECALLED_HOLD (neutral), POST orders/:id/recall {release}.",
    customerVisible: true,
  },
  {
    id: "2026-10-23-split-shipment-lines",
    date: "2026-10-23",
    kind: "fixed",
    area: "Versand",
    title: "Versandmeldung: geteilte Positionen werden vollständig an den Shop gemeldet",
    summary: "Meldete das Lager denselben Artikel in mehreren Positionen (z. B. 1 + 2 + 3 Stück aus verschiedenen Chargen), kam beim Shop nur die letzte Position an – die Bestellung stand dort als „Teilweise erfüllt“. Jetzt werden die Mengen zusammengezählt.",
    details: ["Gilt für Shopify, Amazon und Kaufland", "Betroffene Bestellungen der letzten 120 Tage werden ermittelt und nachgemeldet"],
    internal: "Ursache: new Map(lines.map(l => [l.sku, l.quantity])) überschrieb statt zu summieren. Jetzt sumShipmentLines (domain). Suchlauf repair.scanSplitShipments (einmalig, nur lesend) schreibt die Liste ins Worker-Log; Nachmelden erst nach Freigabe.",
    customerVisible: true,
  },
  {
    id: "2026-10-21-hold-status",
    date: "2026-10-21",
    kind: "improved",
    area: "Aufträge",
    title: "Zurückgehaltene Aufträge: Grund auf einen Blick",
    summary: "Statt „Zurückgehalten“ und einem abgeschnittenen Satz nennt der Status gleich den Grund – „Wartet auf Ware“, „Adresse prüfen“, „Zahlung offen“ … – darunter das Wesentliche in Klartext.",
    details: [
      "Bei fehlender Ware: Artikelname statt Artikelnummer, z. B. „Trolleytasche · 2 fehlen“",
      "Gewollte Wartezeiten (Freigabetermin, Testbetrieb, Produktion) neutral statt gelb",
      "Mehrere Gründe: „+1“, der volle Wortlaut beim Drüberfahren",
    ],
    customerVisible: true,
  },
  {
    id: "2026-10-21-line-availability",
    date: "2026-10-21",
    kind: "new",
    area: "Aufträge",
    title: "Verfügbarkeit je Position im Auftrag",
    summary: "Solange ein Auftrag noch nicht beim Lager ist, zeigt jede Position, ob ihr Bestand reserviert ist oder fehlt – und wer den Bestand gerade hält.",
    details: [
      "„Reserviert“ bzw. „Fehlt 2“ mit Füllstand direkt in der Positionstabelle (grün reserviert · schraffiert bei älteren Bestellungen · blau im Zulauf · leer fehlt ganz), oben „1 von 2 Positionen lieferbar“",
      "Liegt Bestand im Lager, steht aber älteren Bestellungen zu: welche (mit Link) und wie viel",
      "„Vorziehen – jetzt beliefern“, wenn das die Position sofort lieferbar macht (bewusst gegen die Reihenfolge)",
      "Angekündigte Wareneingänge werden angezeigt",
      "Nach der Übergabe: oben „Im Lager reserviert“, „Wird kommissioniert“ oder „1 von 2 Positionen versendet“; je Position nur bei Teilversand oder mehreren Paketen – versendet mit Paket, teilweise, noch im Lager",
    ],
    customerVisible: true,
  },
  {
    id: "2026-10-21-picking-cancelled",
    date: "2026-10-21",
    kind: "fixed",
    area: "Aufträge",
    title: "Abgebrochene Kommissionierung wird erkannt",
    summary: "Bricht das Lager die Kommissionierung ab und setzt den Auftrag zurück auf „bestätigt“, stand er im Portal weiter auf „Picking läuft“ – Storno und Freigabetermin waren gesperrt. Jetzt geht er zurück auf „Übertragen“.",
    details: [
      "Rücksprung aus der Kommissionierung wird übernommen und im Verlauf vermerkt",
      "Stündlicher Gegencheck: Aufträge, die seit über 2 Stunden „In Bearbeitung“ stehen, werden live im Lager geprüft",
      "Versendete Aufträge werden nie zurückgesetzt",
    ],
    customerVisible: true,
  },
  {
    id: "2026-10-21-kpis-shop-orders",
    date: "2026-10-21",
    kind: "fixed",
    area: "Aufträge",
    title: "Kennzahlen zählen Shop-Bestellungen statt Fulfillment-Anfragen",
    summary: "Wurde eine Bestellung im Shop storniert und neu angefordert oder aus mehreren Standorten geliefert, zählte sie in den Kennzahlen doppelt – inklusive Bestellwert. Jetzt zählt jede Shop-Bestellung einmal.",
    details: [
      "Aufträge und Warenwert: je Shop-Bestellung einmal",
      "Artikel bestellt: ersetzte Anfragen zählen nicht mehr mit",
      "Leistung und Berichte: eingegangene Aufträge ebenso je Shop-Bestellung",
      "Versendet und Zugestellt bleiben je Sendung",
    ],
    internal: "SQL-Hilfen replacedOrderCond / firstOfShopOrderCond in packages/db/src/shop-orders.ts. Profit Center, Verkaufszahlen, Kalender, Ladenhüter schlossen stornierte schon aus.",
    customerVisible: true,
  },
  {
    id: "2026-10-20-shop-order-requests",
    date: "2026-10-20",
    kind: "improved",
    area: "Aufträge",
    title: "Eine Zeile je Shopify-Bestellung – ersetzte Anfragen aufklappbar",
    summary: "Wird im Shop der Standort geändert oder eine Anfrage storniert und neu gestellt, entsteht je Anfrage ein Auftrag (#8349, #8349-2 …). Die Liste und der Auftrag zeigen jetzt, was zusammengehört und welcher Auftrag gilt.",
    details: [
      "Eine Zeile je Bestellung: im Shop stornierte und neu angeforderte Anfragen heißen „Ersetzt“ und stehen aufklappbar unter dem aktuellen Auftrag („1 frühere Anfrage“) – nicht mehr unter „Storniert“, ohne doppelten Betrag",
      "„Ersetzte anzeigen“ zeigt sie bei Bedarf wieder als eigene Zeilen; die Suche findet immer alle",
      "„Ersetzt“ nur, wenn die neue Anfrage nach dem Storno kam – parallele Anfragen (z. B. aus zwei Standorten) ersetzen nichts",
      "Laufen mehrere Anfragen gleichzeitig (z. B. aus zwei Standorten): „2 Anfragen“ am Auftrag",
      "Im Auftrag: alle Anfragen der Bestellung mit Stand, Stückzahl und Zeitpunkt, anklickbar; ein ersetzter Auftrag zeigt oben, als welcher es weitergeht",
    ],
    internal: "„Ersetzt“ ist nur Anzeige: Status bleibt cancelled (Lager, Shopify-Abgleich, Abrechnung unverändert). Erkennung: storniert + spätere, nicht stornierte Anfrage mit gleichem channel_order_ref (replacedOrderSql).",
    customerVisible: true,
  },
  {
    id: "2026-10-20-warehouse-number-taken",
    date: "2026-10-20",
    kind: "fixed",
    area: "Aufträge",
    title: "Übergabe ans Lager hing bei doppelter Lager-Nummer",
    summary: "Hatte ein erneut angefragter Auftrag im Lager schon die Nummer „#8340-2“ und kam später eine weitere Bestellung mit derselben Nummer, blieb diese bei „Wird übertragen“ hängen. Jetzt bekommt sie automatisch die nächste freie Lager-Nummer und geht ans Lager.",
    details: [
      "Neue Auftragsnummern beachten auch Lager-Nummern erneut angefragter Aufträge",
      "Ein vorhandener Lagerauftrag wird nur übernommen, wenn er wirklich zu diesem Auftrag gehört",
      "Ist die Nummer im Lager schon an eine andere Bestellung vergeben: nächste freie Nummer, Vermerk im Verlauf, neu übertragen",
      "Ist der vorhandene Lagerauftrag noch aktiv und gehört zur selben Shopify-Bestellung (alter Auftrag bei uns storniert, gleiche Positionen): er wird übernommen statt doppelt angelegt – sonst „prüfen“, nie ein zweiter Lagerauftrag",
    ],
    internal: "Ursache: nextFreeOrderNumber prüfte nur orders.order_number, nicht warehouse_order_number (Rerequest „-2“). Die Idempotenz-Abfrage in createOutbound (GET per Nummer) übernahm dann den Ausgang des anderen Auftrags → Unique external_refs_external, Job in Dauerschleife. Jetzt Vergleich externalNumber = Auftrags-ID, sonst numberTaken → nextWarehouseNumber.",
    customerVisible: true,
  },
  {
    id: "2026-10-19-error-page",
    date: "2026-10-19",
    kind: "fixed",
    area: "Portal",
    title: "Keine leeren Seiten mehr durch Browser-Übersetzung",
    summary: "Die automatische Übersetzung des Browsers und manche Erweiterungen konnten Seiten zum Absturz bringen („… is not a child of this node“). Das ist abgefangen; falls doch einmal etwas schiefgeht, gibt es eine verständliche Fehlerseite mit „Neu laden“.",
    details: [
      "Das Portal gibt es auf Deutsch und Englisch – die Browser-Übersetzung ist dafür abgeschaltet",
      "Eingriffe von Erweiterungen (z. B. Grammarly) in die Seite führen nicht mehr zum Absturz",
      "Fehlerseite innerhalb der Navigation: das Menü bleibt bedienbar",
    ],
    internal: "Ursache: React-Router hatte kein errorElement → nackte Standardmeldung. Jetzt ErrorPage + dom-guard (removeChild/insertBefore, facebook/react#11538) + translate=\"no\". Abstürze landen als Warnung „portal client error“ im API-Log (POST /api/client-errors, 20/h je IP, nichts in der DB).",
    customerVisible: true,
  },
  {
    id: "2026-10-16-ffn-connect-migration",
    date: "2026-10-16",
    kind: "new",
    area: "Shopify",
    title: "Umzug offener Bestellungen von FFN Connect",
    summary: "Logiful-Admins holen offene Shopify-Bestellungen, die noch bei FFN Connect liegen, auf Knopfdruck zu Logiful – ohne zeitlichen Versatz in der Bestandsvergabe.",
    details: [
      "Vorschau: offene Bestellungen mit Bestelldatum und Lagerauftrag in FFN; in Kommissionierung bleibt bei FFN Connect",
      "Je Bestellung: Stornierungsanfrage an FFN Connect (Bestellung selbst bleibt unverändert) → Prüfung im Lager → Logiful-Standort → bei Logiful angefordert",
      "Verschoben wird erst, wenn der Lagerauftrag von FFN Connect im Lager storniert ist – kein Doppelversand",
      "Von FFN Connect abgelehnte Fulfillment-Anfragen (in Shopify offen) werden ohne Stornierungsanfrage direkt verschoben – ebenfalls erst nach Prüfung im Lager",
      "Lageraufträge mit Präfix von FFN Connect (z. B. 4171-7596 für #7596, auch neue Anläufe wie 4171-7596-2) werden erkannt; Präfixe anderer Shops auf demselben FFN-Konto bleiben außen vor, mehrere aktive Treffer gehen auf „prüfen“",
      "Versendete Teillieferungen und eigene Logiful-Aufträge zählen bei der Zuordnung nicht mit; Hinweise nennen Nummer und Stand der Lageraufträge",
      "Während des Umzugs ist die Übergabe ans Lager für den Kunden angehalten (höchstens 3 Stunden); danach Bestandsvergabe streng nach Shopify-Bestelldatum",
    ],
    internal: "Shop-Seite → Karte „Umzug von FFN Connect“ (nur staff_admin). Erst mit EINER Bestellung testen: Shopify-Rechte (Stornierungsanfrage, fulfillmentOrderMove) und Reaktionszeit von FFN Connect sind vorher nicht geprüft. Job shopify.migrationTick jede Minute, Tabelle channel_migrations, Halt MIGRATION_HOLD.",
    customerVisible: false,
  },
  {
    id: "2026-10-15-default-weight",
    date: "2026-10-15",
    kind: "new",
    area: "Artikel",
    title: "Standardartikelgewicht",
    summary: "Unter Artikelregeln lässt sich ein Standardgewicht hinterlegen. Es gilt für alle Artikel ohne eigenes Gewicht – sofort auch für bestehende – und geht ans Lager. Im Artikel ist es als „Standardgewicht“ gekennzeichnet und lässt sich jederzeit überschreiben.",
    details: ["Eigene Gewichte aus Shop, Gewichtsregel oder von Hand haben Vorrang", "Standard ändern: alle Artikel mit Standardgewicht ziehen automatisch nach", "Im Artikel: „Zurück auf Standardgewicht“ verwirft ein von Hand gepflegtes Gewicht", "Standard entfernen: Artikel behalten ihr Gewicht als festen Wert"],
    internal: "tenants.default_weight_kg; Markierung products.weight_default_kg (Standard, solange Gewicht gleich und nicht geschützt). Läuft in applyProductRules nach den Regeln (alle Import-Wege), nachträglich über den Job products.applyRules. Kanal-Gewicht 0 überschreibt nicht mehr.",
    customerVisible: true,
    to: "/rules/products",
  },
  {
    id: "2026-10-14-overdue-pause",
    date: "2026-10-14",
    kind: "new",
    area: "Abrechnung",
    title: "Aufträge pausieren bei überfälligen Rechnungen",
    summary: "Optional zur Warnung: Ab einer späteren Grenze gehen neue Aufträge eines Kunden nicht mehr ans Lager, bis die überfälligen Rechnungen bezahlt sind. Das Banner kündigt die Pause vorher mit Datum an.",
    details: ["Global und je Kunde: an/aus, ab wie vielen Tagen (später als die Warnung – die Differenz ist die Vorwarnzeit), eigener Bannertext während der Pause", "Bereits übertragene Aufträge laufen weiter; pausierte stehen im Handlungsbedarf", "Nach dem Zahlungseingang gibt der stündliche Abgleich (oder „Jetzt abgleichen“) die Aufträge automatisch frei", "„Übertragung erzwingen“ durch den Kunden umgeht die Pause nicht – Ausnahmen setzt Logiful je Kunde"],
    internal: "Standard AUS. Vor dem Einschalten Zahlungsverbuchung in der Abrechnung prüfen (Stand 14.10.: 133 unbezahlte Rechnungen bei 67 Kunden, viele vermutlich bezahlt, aber nicht verbucht). Haltegrund BILLING_HOLD, Job billing.recheckHolds nach jedem Abgleich und jeder Einstellungsänderung.",
    customerVisible: false,
    to: "/admin/customers",
  },
  {
    id: "2026-10-14-overdue-invoices",
    date: "2026-10-14",
    kind: "new",
    area: "Abrechnung",
    title: "Warnung bei überfälligen Rechnungen",
    summary: "Sind Rechnungen von Logiful überfällig, zeigt das Portal ganz oben ein Banner mit Hinweis und Link zur neuen Seite „Offene Rechnungen“.",
    details: ["Global und je Kunde einstellbar: an/aus, ab wie vielen Tagen nach Rechnungsdatum, wer das Banner sieht, Text (Deutsch/Englisch)", "Lastschriften, die noch laufen, zählen nicht als überfällig", "Abgleich mit der Abrechnung stündlich – bezahlte Rechnungen verschwinden automatisch"],
    internal: "Standard ist AUS – unter Logiful intern → Kunden → „Überfällige Rechnungen“ einschalten. Quelle: billing_run (Retool, nur lesend, Bezahlt-Logik wie Kostenübersicht); Fälligkeit = Rechnungsdatum + eingestellte Tage. Reine Warnung, Aufträge laufen weiter.",
    customerVisible: false,
    to: "/admin/customers",
  },
  {
    id: "2026-10-13-order-release-date",
    date: "2026-10-13",
    kind: "new",
    area: "Aufträge",
    title: "Freigabetermin: Aufträge erst ab einem Datum versenden",
    summary: "Ist der Endkunde z. B. zwei Wochen im Urlaub, setzt ihr im Auftrag einen Freigabetermin. Der Auftrag wartet bis dahin, die Ware bleibt für ihn reserviert, und am Termin geht er automatisch ans Lager.",
    details: [
      "Schon im Lager? Das Portal prüft live und storniert dort, solange die Kommissionierung noch nicht begonnen hat – am Termin wird neu übertragen",
      "Termin ändern oder aufheben jederzeit im Auftrag",
      "Bei Logiful stornieren jetzt auch für Marktplatz-, Odoo- und SFTP-Aufträge – die Bestellung im Kanal bleibt unverändert",
    ],
    internal: "orders.deferred_until; Halt „Freigabetermin: …“ zählt nicht als Handlungsbedarf. Freigabe per verzögertem Job, stündliches Sicherheitsnetz orders.releaseDeferred. Im Lager: cancelOutbound + rerequest (Lager-Nr. -2). „Übertragung erzwingen“ hebt den Termin auf.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-12-prospect-view",
    date: "2026-10-12",
    kind: "new",
    area: "Kunden",
    title: "Interessenten-Ansicht: was Neukunden vor der Freischaltung sehen",
    summary: "Selbst registrierte Kunden sehen bis zur Freischaltung nur die Bereiche, die Logiful freigibt – Standard: Shops verbinden, Artikel, Aufträge ansehen, Team. Regeln, Versand, Module & Preise, Auswertungen, Schnittstellen und Partner bleiben gesperrt.",
    details: ["Gesperrtes liefert auch die Schnittstelle nicht aus – nicht nur ausgeblendet", "Standard in der Kundenverwaltung (Logiful-Admins), Ausnahmen je Interessent (ganzes Team)", "Neue Funktionen sind für Interessenten gesperrt, bis sie freigegeben werden", "Mit dem Beenden des Testbetriebs sieht der Kunde sofort alles"],
    internal: "Interessent = tenants.signup gesetzt UND Testbetrieb an. Bestandskunden im Testbetrieb (Umstellung) sind nie betroffen. Neue Kunden-Routen müssen in packages/domain/src/prospect-access.ts einem Bereich zugeordnet werden (Test prüft das).",
    customerVisible: false,
    to: "/admin/customers",
  },
  {
    id: "2026-10-12-sales-room-1",
    date: "2026-10-12",
    kind: "new",
    area: "Vertrieb",
    title: "Sales Room in Connect (Etappe 1): Angebot per Link",
    summary: "Interessenten öffnen ihr Angebot aus dem Sales Deal Manager unter app.logiful.de/angebot/… – mit Begrüßung, Konditionen, PDF (Deutsch/Englisch), Pick-&-Pack-Rechner und FAQ. Ohne Anmeldung, nur über den persönlichen Link.",
    details: ["Gezeigt wird immer das neueste versendete Angebot – nie Entwürfe", "Klicks aus der Angebotsmail landen wie bisher im Mail-Tracking des Deal Managers", "Nächste Etappe: Firmendaten, Vertrag mit Unterschrift und Onboarding direkt im Sales Room"],
    internal: "Daten live aus der Retool-Datenbank (SALES_DATABASE_URL, nur Produktion). Retool-Änderung für den Mail-Link: docs/sales-room/RETOOL-ANALYSE.md §10 – public_app_url nicht ändern.",
    customerVisible: false,
  },
  {
    id: "2026-10-12-self-signup",
    date: "2026-10-12",
    kind: "new",
    area: "Kunden",
    title: "Neukunden registrieren sich selbst",
    summary: "Neue Kunden legen ihr Logiful-Konto unter /registrieren selbst an – mit freiwilligen Angaben fürs Angebot (Aufträge, Artikel, Kanäle, Besonderheiten, Start). Das Konto startet im Testbetrieb: alles einrichten, aber nichts geht ans Lager, bis Logiful freischaltet.",
    details: ["Logiful-Admins bekommen bei jeder Registrierung eine Mail mit Kontakt und Angaben", "In der Kundenverwaltung markiert „Selbst registriert“; Angaben per Mauszeiger", "Gleiche Registrierung in der Shopify-App", "Schutz: höchstens 5 Registrierungen je Stunde und Adresse, Lockfeld gegen Bots"],
    internal: "Freischalten = Testbetrieb in der Kundenverwaltung beenden (vorher FFN-Verknüpfung und Vertrag). Link für die Website: https://app.logiful.de/registrieren",
    customerVisible: false,
    to: "/admin/customers",
  },
  {
    id: "2026-10-12-partner-team",
    date: "2026-10-12",
    kind: "improved",
    area: "Partner Network",
    title: "Partner anlegen und bearbeiten: ganzes Logiful-Team",
    summary: "Nicht mehr nur Logiful-Admins, sondern alle Logiful-Mitarbeitenden können Partner anlegen und bearbeiten.",
    internal: "Löschen von Partnern sowie Status/Provision von Anfragen bleiben bei den Logiful-Admins.",
    customerVisible: false,
    to: "/admin/partners",
  },
  {
    id: "2026-10-12-shopify-app-config",
    date: "2026-10-12",
    kind: "improved",
    area: "Shopify",
    title: "Öffentliche Shopify-App: Konfiguration und zusätzliche Rechte per Klick",
    summary: "Händler der öffentlichen App erteilen empfohlene Zusatzrechte (z. B. volle Bestellhistorie) direkt auf der Logiful-Startseite in Shopify – ohne Neuinstallation. Der Shop-Check verweist dorthin.",
    internal: "shopify.app.logiful.toml (eingebettet, verwaltete Installation, Pflicht- und optionale Scopes, Webhooks inkl. orders/paid und Datenschutz). Abgleich mit dem Code per Test. read_all_orders braucht eine Freigabe im Partner-Dashboard.",
    customerVisible: false,
  },
  {
    id: "2026-10-12-shopify-embedded-app",
    date: "2026-10-12",
    kind: "new",
    area: "Shopify",
    title: "Öffentliche Shopify-App: Startseite im Shopify-Admin und Selbst-Registrierung",
    summary: "Händler installieren Logiful direkt aus Shopify. Die Startseite im Shopify-Admin zeigt den Stand und führt ins Portal; neue Händler legen dort selbst ein Konto an, bestehende Kunden verknüpfen ihren Shop.",
    details: ["Neue Konten starten im Testbetrieb – nichts geht ans Lager, bis Logiful freischaltet", "Logiful-Administratoren bekommen bei jeder Registrierung eine Mail", "Shops mit eigener Custom App bleiben unverändert – der Umzug erfolgt begleitet"],
    internal: "Erst aktiv, wenn die öffentliche App im Partner-Dashboard angelegt ist und SHOPIFY_CLIENT_ID/SECRET auf sie zeigen (Plan: docs/shopify/PUBLIC-APP.md, Pakete 5–7). Routen: /shopify/app (eingebettet, App Bridge), /shopify/app/session (ID-Token + Token-Tausch), /api/shopify/claim/* und Portalseite /shop-verbinden. Nicht verknüpfte Installationen liegen in shopify_installs.",
    customerVisible: false,
  },
  {
    id: "2026-10-12-shopify-public-prep",
    date: "2026-10-12",
    kind: "improved",
    area: "Shopify",
    title: "Vorbereitung öffentliche Shopify-App: Datenschutz-Anfragen und neue Zugangsschlüssel",
    summary: "Auskunfts- und Löschanfragen von Endkunden aus Shopify werden jetzt vollständig bearbeitet: Löschungen automatisch, Auskünfte zum Herunterladen unter Einstellungen → Datenschutz-Anfragen (mit Hinweis im Handlungsbedarf).",
    details: ["Ablaufende Shopify-Zugangsschlüssel werden automatisch erneuert (Pflicht für öffentliche Apps)", "Nach einer Deinstallation enden die regelmäßigen Abgleiche des Shops"],
    internal: "Plan und Stand: docs/shopify/PUBLIC-APP.md. Bestehende Custom Apps behalten dauerhafte Schlüssel. Shop-Löschung (48 h nach Deinstallation) anonymisiert alle Aufträge des Shops und entfernt die Zugangsdaten; Aufträge selbst bleiben für Abrechnung/Aufbewahrung.",
    customerVisible: true,
    to: "/settings/privacy",
  },
  {
    id: "2026-10-10-express-priority-fix",
    date: "2026-10-10",
    kind: "fixed",
    area: "Aufträge",
    title: "Express-Priorität bleibt jetzt erhalten",
    summary: "Ein manuell gesetztes Express (Auftrag bearbeiten, Sammelaktion, manuelle Aufträge, Public API) wurde bei der anschließenden Verarbeitung wieder auf 0 gesetzt, wenn keine Kundenregel eine Priorität vergab – das Lager bekam die Priorität dadurch nicht. Jetzt bleibt sie erhalten.",
    details: ["Setzt eine Kundenregel eine Priorität, gewinnt weiterhin die Regel", "Bereits ans Lager übergebene Aufträge werden nicht nachträglich geändert"],
    internal: "Ursache: evaluateOrderRules nahm die Priorität nur aus den Regeln (Fallback 0) und der Worker schrieb das zurück. Betroffene Aufträge der Vergangenheit sind mit Priorität 0 an JTL-FFN gegangen.",
    customerVisible: false,
  },
  {
    id: "2026-10-10-tracking-page",
    date: "2026-10-10",
    kind: "new",
    area: "Track & Trace",
    title: "Neues Modul: Tracking-Seite mit Composer",
    summary: "Kunden bauen ihre eigene Sendungsverfolgung für Endkunden: Logo, Farben, Schrift, Titelbanner, animierter Statusverlauf, Zustellprognose, Paketinhalt, Gutscheine, Produktempfehlungen, FAQ, Buttons, Social Media und Newsletter – mit Live-Vorschau je Status, Gerät und Sprache.",
    details: [
      "Einstieg wählbar: Link in der Shopify-Versandmail (statt DHL-Link) und/oder Suche nach Bestellnummer + PLZ/E-Mail",
      "Deutsch/Englisch automatisch nach Lieferland, Texte je Sprache pflegbar",
      "Bausteine nur in bestimmten Phasen anzeigen (z. B. Kontakt nur bei Zustellproblem)",
      "Eigene Domain per CNAME, Prüfung und SSL automatisch",
      "Datenschutz: Endkunden sehen nie Name, Straße, E-Mail oder Preise",
    ],
    internal: "Modul „tracking_page“ (Pakete: Tracking-Seite / mit eigener Domain, noch nicht selbst buchbar – Preise unter Logiful intern → Module). Eigene Domains: RENDER_API_KEY und RENDER_SERVICE_ID am Web-Dienst setzen, sonst bleibt die Domain „in Freischaltung“ und Logiful aktiviert sie von Hand (POST /api/admin/tracking-pages/:tenantId/domain/activate). Status kommt aus Track & Trace (DHL) – ohne dieses Modul zeigt die Seite nur Bestellt/Versendet.",
    customerVisible: true,
    to: "/tracking/page",
  },
  {
    id: "2026-10-09-english-portal",
    date: "2026-10-09",
    kind: "improved",
    area: "Portal",
    title: "Portal vollständig auf Englisch",
    summary: "Alle Kundenseiten, die Hilfe und der Rundgang sind jetzt auf Englisch verfügbar (Sprachschalter unten links) – einschließlich der Meldungen vom Server wie Haltegründe, Prüfhinweise an Artikeln, Verlauf und Fehlermeldungen.",
    details: ["Neue Texte ohne Übersetzung lässt die automatische Prüfung nicht mehr durch – auch neue Servermeldungen", "Eigene Regelnamen und Freitexte der Kunden bleiben, wie sie eingegeben wurden"],
    internal: "Logiful-interne Seiten bleiben deutsch. E-Mails an Kunden/Endkunden sind noch deutsch. Servertexte: apps/portal/scripts/server-texts.mjs --write sammelt neue Meldungen ein, Übersetzung in src/locales/server-en.json.",
    customerVisible: true,
  },
  {
    id: "2026-10-09-method-labels",
    date: "2026-10-09",
    kind: "improved",
    area: "Aufträge",
    title: "Verständliche Versandarten im Auftrag",
    summary: "Im Auftrag steht jetzt die Versandart, wie man sie kennt – z. B. „DHL Paket“ oder „DHL Kleinpaket“ – statt des internen Lagernamens. Das Lager erscheint als Kürzel (Name beim Drüberfahren).",
    internal: "Bezeichnung je Versandart unter Logiful intern → Versand → Versandart öffnen → „Bezeichnung für Kunden“ pflegen (auch je Kunde im Kundenprofil). Ohne Bezeichnung bleibt der FFN-Name sichtbar. Logiful sieht im Auftrag zusätzlich „im Lager: …“.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-channel-remove",
    date: "2026-10-08",
    kind: "new",
    area: "Shops",
    title: "Verbindungen entfernen",
    summary: "Auf jeder Shop-/Kanal-Kachel gibt es jetzt „Verbindung entfernen“. Ohne Aufträge (z. B. versehentlich angelegt) wird die Verbindung gelöscht, mit Aufträgen nur getrennt – die Auftragshistorie bleibt, es wird nichts mehr abgeglichen.",
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-08-sku-spaces",
    date: "2026-10-08",
    kind: "fixed",
    area: "Artikel",
    title: "SKUs mit Leerzeichen erlaubt",
    summary: "Artikelnummern mit Leerzeichen in der Mitte (z. B. „P58 SO“) blockieren nicht mehr. Gesperrt sind nur noch unsichtbare Zeichen wie Tabulator oder geschützte Leerzeichen (oft aus Copy & Paste) sowie Leerzeichen am Anfang oder Ende.",
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-08-profit-manual-costs",
    date: "2026-10-08",
    kind: "new",
    area: "Profit Center",
    title: "Profit Center: eigene Kosten erfassen",
    summary: "Unter „Weitere Kosten“ lassen sich monatliche Fixkosten (Software, Marketing, Personal, Miete …) und Kosten zu einzelnen Aufträgen (Nachversand, Kulanz, Sonderverpackung …) eintragen. Auftragskosten mindern den Deckungsbeitrag der Bestellung, Fixkosten werden tagesgenau auf den Zeitraum umgelegt – daraus ergibt sich das „Ergebnis nach Fixkosten“.",
    details: ["Kosten zu einer Bestellung auch direkt in der Übersicht über „+“", "Fixkosten mit Beginn und optionalem Ende (z. B. befristete Kampagne)", "Beträge netto"],
    customerVisible: true,
    to: "/profit?tab=extra",
  },
  {
    id: "2026-10-08-carrier-delay",
    date: "2026-10-08",
    kind: "fixed",
    area: "Track & Trace",
    title: "Kein „Zustellproblem“ mehr, wenn DHL selbst neu zustellt",
    summary: "Meldet DHL eine Verzögerung aus eigenen Gründen (Krankheit, Unwetter, „unerwartete Umstände“) und kündigt einen neuen Zustellversuch an, gilt die Sendung weiter als unterwegs – sie erscheint nicht mehr im Handlungsbedarf. Echte Probleme beim Empfänger (unbekannt, Annahme verweigert, Adresse falsch) bleiben gemeldet.",
    customerVisible: true,
    to: "/tracking",
  },
  {
    id: "2026-10-08-partner-network",
    date: "2026-10-08",
    kind: "new",
    area: "Partner Network",
    title: "Partner Network: ausgewählte Partner mit Vorteilen für Logiful-Kunden",
    summary: "Neuer Menüpunkt „Partner Network“: Partner für Verpackung, Fotografie, Finanzierung, Marketing u. a. als Kacheln – mit Beschreibung, Vorteil und Rabattcode. Ein Klick auf „Angebot anfragen“ schickt eure Anfrage (mit eurer Zustimmung) direkt an den Partner.",
    details: ["Ihr entscheidet bei jeder Anfrage, welche Kontaktdaten weitergegeben werden", "Manche Angebote führen direkt auf die Seite des Partners"],
    internal: "Pflege unter Logiful intern → Partner Network (nur Logiful-Admins): Kachelgröße, Farbe, Logo/Titelbild, Vorteil, Zielgruppe (Kanäle), Ablauf (Anfrage per Mail oder Link) und Provision (fest je Anfrage + % vom Abschluss). Anfragen erscheinen dort mit Status und Abschlusswert, die Provision wird berechnet; Bedingungen werden bei der Anfrage eingefroren. Logiful-Admins bekommen bei jeder Anfrage eine Mail.",
    customerVisible: true,
    to: "/partners",
  },
  {
    id: "2026-10-08-duplicate-ean",
    date: "2026-10-08",
    kind: "improved",
    area: "Artikel",
    title: "Doppelte EAN: betroffene Artikel auf einen Blick",
    summary: "Mit dem Filter „Doppelte EAN“ stehen Artikel mit derselben EAN direkt untereinander und sind farbig zusammengefasst. Unter jeder EAN steht „auch bei …“ mit den anderen betroffenen Artikeln – ein Klick öffnet sie. Auch im Artikel selbst sind sie beim Hinweis verlinkt.",
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-08-sku-characters",
    date: "2026-10-08",
    kind: "fixed",
    area: "Artikel",
    title: "SKUs mit Komma & Sonderzeichen nicht mehr blockiert",
    summary: "Artikelnummern wie „L-0768-1,5“ galten als ungültig, obwohl das Lager sie annimmt. Gesperrt sind jetzt nur noch Leer- und unsichtbare Steuerzeichen; betroffene Artikel werden automatisch neu geprüft und übertragen.",
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-08-warehouse-code",
    date: "2026-10-08",
    kind: "new",
    area: "Aufträge",
    title: "Lager auf einen Blick",
    summary: "Die Auftragsliste hat eine neue Spalte „Lager“ mit dem Kürzel des versendenden Lagers; im Auftrag steht es unter „Versand“. Angepasste Spaltenauswahlen bekommen die neue Spalte automatisch dazu (über „Spalten“ ausblendbar).",
    internal: "Kürzel kommt automatisch aus dem Lagernamen („Logiful PDM“ → PDM), sonst aus dem Ort; unter Logiful intern → Kunden → Lagerstandorte überschreibbar (2–6 Zeichen). Der FFN-Abgleich behält ein festgelegtes Kürzel.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-partial-shipping-status",
    date: "2026-10-08",
    kind: "fixed",
    area: "Aufträge",
    title: "Teilversand richtig angezeigt",
    summary: "Schickt das Lager erst einen Teil eines Auftrags los, steht der Auftrag jetzt auf „Teilversendet“ statt schon auf „Versendet“ bzw. „Zugestellt“. Erst wenn alle Positionen unterwegs sind, wechselt er auf „Versendet“. Bei Teillieferungen zeigt der Ursprungsauftrag „Rest offen“, solange die Restlieferung (#…-T2) noch wartet.",
    details: ["Sets zählen als versendet, wenn das Set oder alle seine Bestandteile dabei sind", "Meldet das Lager den Auftrag als komplett versendet, gilt das", "Kennzahl „Zugestellt“ zählt teilversendete Aufträge erst, wenn alles da ist"],
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-order-kpi-strip",
    date: "2026-10-08",
    kind: "new",
    area: "Aufträge",
    title: "Kennzahlen über der Auftragsliste und Status mit Icon",
    summary: "Über den Aufträgen steht jetzt eine Kennzahlen-Leiste: Aufträge, bestellte Artikel, Versendet, Zugestellt und Zurückgehalten – für heute, die letzten 7 oder 30 Tage, jeweils mit kleinem Verlauf. Der Status jedes Auftrags erscheint als farbige Markierung mit Icon.",
    details: ["Klick auf „Zurückgehalten“ zeigt genau diese Aufträge", "Der gewählte Zeitraum wird im Browser gemerkt"],
    internal: "Welche Kacheln Kunden sehen, legt Logiful intern → Kennzahlen fest (für alle Kunden, nur Logiful-Admins). Heikle Werte (pünktlich versendet, Übergabe bis Versand, Versand bis Zustellung) sind standardmäßig aus; abgeschaltete Kennzahlen berechnet der Server nicht und liefert sie nicht aus.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-tracking-cadence",
    date: "2026-10-08",
    kind: "improved",
    area: "Track & Trace",
    title: "Zustellstatus deutlich aktueller",
    summary: "Pakete werden tagsüber jetzt viel öfter bei DHL abgefragt: erste Abfrage 1 Stunde nach Versand, unterwegs alle 90 Minuten, in Zustellung alle 30 Minuten. „In Zustellung“ und „Zugestellt“ stehen so zeitnah am Auftrag statt bis zu 6 Stunden später.",
    details: ["Nachts (22–6 Uhr) wird nicht abgefragt – DHL scannt dann kaum", "Bei sehr vielen Paketen werden die Abstände gleichmäßig gestreckt, damit das Tageskontingent bis zum Abend reicht"],
    internal: "Solange DHL Parcel DE den App-Schlüssel ablehnt („Invalid ApiKey“), läuft alles über Unified (App-Kontingent DHL_UNIFIED_DAILY_LIMIT, Standard 250/Tag). Mit Freischaltung Parcel DE: 10.000 Sendungen je Konto und Tag.",
    customerVisible: true,
    to: "/tracking",
  },
  {
    id: "2026-10-08-partial-delivery-rules",
    date: "2026-10-08",
    kind: "new",
    area: "Auftragsregeln",
    title: "Teillieferung als Regel – mit Mindestanteil und Höchstzahl an Teilen",
    summary: "Neue Regel-Aktion „Bei Fehlbestand“: teilliefern, zurückhalten oder trotzdem übertragen – je nach Land, Warenwert, Tag, Artikel usw. Teillieferungen lassen sich begrenzen: erst ab einem Mindestanteil (Warenwert) und höchstens N Teile je Auftrag, damit ein Auftrag nicht in viele Pakete zerfällt.",
    details: [
      "Rangfolge: Regel › Shop-Einstellung › Kunden-Einstellung; im Verlauf steht, welche Regel gegriffen hat",
      "Vorschlag für neu gewählte Teillieferung: ab 50 % lieferbar, höchstens 2 Teile",
      "Bisher gespeicherte Einstellungen „Teilliefern“ bleiben ohne Grenze, bis sie angepasst werden",
      "Reihenfolge der Bestandsvergabe gilt unverändert: geteilt wird nur, was nach älteren Aufträgen frei ist",
    ],
    customerVisible: true,
    to: "/rules",
  },
  {
    id: "2026-10-08-items-popover",
    date: "2026-10-08",
    kind: "improved",
    area: "Aufträge",
    title: "Artikel-Vorschau in der Auftragsliste mit Bildern",
    summary: "„2 Artikel ⌄“ öffnet beim Drüberfahren oder Klick eine Karte mit Status, Dienstleister und den Positionen – Produktbild, Name, SKU und Menge.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-orders-list-polish",
    date: "2026-10-08",
    kind: "improved",
    area: "Aufträge",
    title: "Auftragsliste: Shopname, ruhigere Versand-Spalte, Artikel beim Drüberfahren",
    summary: "Shopify-Shops heißen jetzt wie in Shopify statt mit der technischen Kennung. Die Details-Spalte zeigt das Logo des Dienstleisters (DHL, DPD, UPS … statt interner Versandart) als Link zur Sendungsverfolgung – die Nummer steht im Auftrag, Kopieren beim Drüberfahren. Beim Drüberfahren über „2 Artikel“ erscheinen die Positionen.",
    details: ["Ein von Hand vergebener Shopname bleibt unverändert", "Sucht man nach einer Sendungsnummer, wird sie in der Liste hervorgehoben angezeigt"],
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-08-stock-tile-filters",
    date: "2026-10-08",
    kind: "improved",
    area: "Bestände",
    title: "Bestände: Kacheln sind Filter, neuer Filter „Mit Bestand“",
    summary: "Ein Klick auf eine Kachel filtert die Liste (erneuter Klick hebt den Filter auf). Neu: „Mit Bestand“ – nur Artikel mit Ware im Lager, größter Bestand zuerst. Hilft bei Kunden mit vielen tausend Artikeln.",
    customerVisible: true,
    to: "/stock?filter=instock",
  },
  {
    id: "2026-10-07-dashboard-production-profit",
    date: "2026-10-07",
    kind: "new",
    area: "Übersicht",
    title: "Neue Kacheln für die Übersicht: Produktion und Profit Center",
    summary: "Unter Übersicht → Anpassen lassen sich zwei neue Kacheln hinzufügen: „Produktion“ (offene Aufträge, was auf Muster-Freigabe oder Komponenten wartet) und „Profit Center“ (Umsatz, Deckungsbeitrag und Marge der letzten 30 Tage mit den stärksten Artikeln).",
    details: ["Sichtbar nur mit gebuchtem Modul und dem jeweiligen Recht"],
    customerVisible: true,
    to: "/",
  },
  {
    id: "2026-10-07-cancel-at-logiful",
    date: "2026-10-07",
    kind: "improved",
    area: "Aufträge",
    title: "Shop-Aufträge bei Logiful stornieren – mit oder ohne Rückgabe an den Shop",
    summary: "Shopify-Aufträge lassen sich jetzt direkt im Portal bei Logiful stornieren (einzeln und als Sammelaktion): „Bei Logiful stornieren“ storniert im Lager und in Connect, der Shop bleibt unverändert. „… und an Shop zurückgeben“ markiert den Auftrag zusätzlich in Shopify als nicht ausgeführt – der Händler kann selbst versenden oder erneut anfordern.",
    details: ["Storno im Lager wie bisher live geprüft – nur bis zur Kommissionierung", "Erneut im Shop angefordert nach Rückgabe: derselbe Auftrag läuft wieder (im Lager mit Nummer „-2“)", "„Fulfillment erneut bei Logiful anfragen“ (einzeln und als Sammelaktion): derselbe stornierte Auftrag läuft wieder durch alle Regeln – bei Fehlbestand geht er nach dem Wareneingang automatisch ans Lager; nur im Lager (FFN) bekommt er eine neue Nummer „-2“", "Die ganze Bestellung (inkl. Erstattung) weiterhin in Shopify stornieren"],
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-07-production-replenish-disassembly",
    date: "2026-10-07",
    kind: "new",
    area: "Produktion",
    title: "Produktion: automatisch nachproduzieren und Fertigartikel auflösen",
    summary: "An der Produktionsstückliste lässt sich ein Mindestbestand mit Auffüllmenge hinterlegen – Connect beauftragt die Produktion dann stündlich selbst. Neu ist außerdem „Auflösen“: Fertigartikel gehen zu Logiful, werden zerlegt und die Komponenten kommen per Wareneingang zurück auf Lager.",
    details: [
      "Nachproduktion: verfügbar = Lager − Reservierungen + Zulauf + offene Produktion; Menge = Auffüllmenge, mindestens bis zum Mindestbestand; solange ein Auftrag offen ist, kein zweiter",
      "„Von Logiful automatisch annehmen“ (Preis aus der Stückliste) stellt nur Logiful ein – sonst wie gewohnt annehmen",
      "Fehlen Komponenten, steht der Auftrag im Handlungsbedarf (Wareneingänge)",
      "Auflösen: Logiful meldet je Komponente die wiederverwendbare Menge (mit Charge/MHD, falls bekannt) – Einstandspreis bleibt unverändert",
    ],
    internal: "Automatische Aufträge sind mit „automatisch“ markiert. Preis beim Auflösen legt Logiful bei der Annahme fest (kein Vorschlag aus der Stückliste).",
    customerVisible: true,
    to: "/production",
  },
  {
    id: "2026-10-07-storage-cost-per-day",
    date: "2026-10-07",
    kind: "improved",
    area: "Bestände",
    title: "Ladenhüter: Lagerkosten je m³ und Tag, zusätzlich Kosten seit Wareneingang",
    summary: "Der Lagerkosten-Satz wird wie bei Logiful üblich je m³ und Tag erfasst (z. B. 0,62 €). „Lager/Monat“ rechnet Satz × Volumen × 30 Tage; darunter stehen die Kosten seit dem letzten Wareneingang (Satz × Volumen × Liegetage).",
    details: ["Bestände → Ladenhüter → Einstellungen: Feld „Lagerkosten je m³ und Tag“", "Bereits hinterlegte Monatssätze wurden einmalig durch 30 geteilt", "CSV-Liste mit Liegetagen und Kosten seit Wareneingang"],
    internal: "Liegetage zählen ab dem letzten Wareneingang mit eingebuchter Menge in Connect – Artikel ohne Wareneingang in Connect zeigen keine Kosten seit Wareneingang.",
    customerVisible: true,
    to: "/stock?view=slow",
  },
  {
    id: "2026-10-07-production",
    date: "2026-10-07",
    kind: "new",
    area: "Produktion",
    title: "Neues Modul Produktion: Konfektionierung mit Stücklisten und Arbeitsschritten",
    summary: "Kunden legen Produktionsstücklisten an (Komponenten, eigenes Material, Material von Logiful, Arbeitsschritte mit Anleitung und Fotos) und beauftragen die Produktion im Portal. Die Komponenten sind sofort reserviert; Logiful nimmt an, startet, lässt ggf. ein Muster freigeben und meldet fertig – die Fertigware kommt per Wareneingang mit Charge und MHD auf Lager.",
    details: [
      "Menü „Produktion“ (Modul freischalten unter Logiful intern → Module & Preise, eigenes Recht „Produktion“)",
      "Preise je Arbeitsschritt (je Stück, pauschal, je Minute) und für Logiful-Material setzt nur Logiful; bei der Annahme ist das Angebot frei änderbar",
      "Start: Komponenten gehen als Auftrag (Kanal „Produktion“) an die Adresse „Logiful Produktion“ – Adresse einmalig unter Produktion → Einstellungen hinterlegen",
      "Fertigmeldung: Gutmenge, Ausschuss mit Grund, Charge, MHD (Vorschlag: frühestes MHD der Komponenten) → Wareneingang wird angelegt und ans Lager gemeldet; Herstellkosten fließen ins Profit Center",
    ],
    internal: "Übergangslösung bis zum eigenen WMS: Weitergabe über FFN (Ausgang + Wareneingang). Bitte Produktionen in FFN nicht von Hand anlegen. Abrechnung: noch keine automatische Übergabe an die Abrechnung – abgerechnete Aufträge in Connect markieren.",
    customerVisible: true,
    to: "/production",
  },
  {
    id: "2026-10-07-returns-relink",
    date: "2026-10-07",
    kind: "fixed",
    area: "Retouren",
    title: "Retouren-Auswertung: Rücksendungen werden nachträglich Artikeln zugeordnet",
    summary: "Retouren, die ankamen, bevor der Artikel im Portal existierte, werden jetzt bei jedem Abgleich (alle 5 Minuten) über die FFN-Artikelnummer bzw. SKU nachträglich zugeordnet. Die Auswertung zeigt die häufigsten nicht zugeordneten SKUs und erklärt eine fehlende Quote.",
    details: ["Quote „—“: Im Zeitraum sind keine Verkäufe bekannt (z. B. Testbetrieb – Testaufträge zählen nicht – oder Shop-Historie nicht importiert)", "„Keine Gründe erfasst“: Gründe vergibt das Lager nur bei Zustand B/C"],
    customerVisible: true,
    to: "/returns",
  },
  {
    id: "2026-10-07-tracking-unified-zip",
    date: "2026-10-07",
    kind: "fixed",
    area: "Track & Trace",
    title: "Sendungsverfolgung über DHL-Ersatzweg mit Empfänger-PLZ",
    summary: "Solange DHL die Hauptschnittstelle (Parcel DE Tracking) nicht freigeschaltet hat, läuft die Verfolgung über „Shipment Tracking – Unified“. Die Abfrage schickt jetzt die Empfänger-PLZ mit – ohne sie liefert DHL zu DHL-Paket-Sendungen oft keine Daten.",
    details: ["Pakete ohne Status werden einmalig sofort neu abgefragt", "Liefert DHL weiterhin nichts, steht im Worker-Log „DHL Unified ohne Daten“ mit Beispielnummern"],
    internal: "Ursache bleibt die fehlende Freischaltung von Parcel DE Tracking für die App (DHL: „Invalid ApiKey“) – beim DHL-Support nachhaken. Unified-Kontingent: 250 Abfragen/Tag.",
    customerVisible: false,
    to: "/admin/carrier-accounts",
  },
  {
    id: "2026-10-07-stock-empty-snapshot",
    date: "2026-10-07",
    kind: "fixed",
    area: "Bestände",
    title: "Bestand fällt nicht mehr auf 0, wenn das Lager kurz eine leere Liste liefert",
    summary: "Der stündliche Bestandsabgleich mit dem Lager übernimmt keine leere oder stark lückenhafte Bestandsliste mehr. Einzelne Artikel, die im Lager fehlen, werden erst auf 0 gesetzt, wenn sie zweimal hintereinander fehlen.",
    details: ["Leere Liste oder mehr als die Hälfte der Artikel mit Bestand fehlt → Abgleich wird übersprungen und wiederholt, nichts geht als 0 an die Shops", "Chargen/MHD bleiben bei leerer Antwort ebenfalls erhalten"],
    internal: "Anlass: FFN lieferte am 7.10. um 04:53 und 12:07 (UTC) für Kokoro eine leere Bestandsliste – alle 12 Artikel standen auf 0 und waren im Shop ausverkauft. Übersprungene Abgleiche erscheinen als fehlgeschlagener Job „Bestandsabgleich mit dem Lager“.",
    customerVisible: false,
    to: "/stock",
  },
  {
    id: "2026-10-07-validation-settings",
    date: "2026-10-07",
    kind: "new",
    area: "Logiful intern",
    title: "Stammdaten-Prüfung je Kunde: was Aufträge zurückhält",
    summary: "Je Kunde einstellbar, welche fehlenden oder fehlerhaften Angaben einen Artikel sperren (er geht dann nicht ans Lager, Aufträge werden zurückgehalten): „hält zurück“, „nur Hinweis“ oder „aus“ – plus „Zolldaten prüfen“.",
    details: ["Einstellungen → Aufträge & Versand → Stammdaten-Prüfung (Kunden sehen sie, ändern kann nur Logiful)", "Fest und nicht einstellbar: SKU, Name, Gewicht, Sets, Gebinde (braucht das Lager) und Gefahrgut", "Nach dem Speichern: alle Artikel neu geprüft – jetzt freie gehen ans Lager, wartende Aufträge laufen automatisch weiter", "Jede Änderung steht im Änderungsprotokoll des Kunden"],
    internal: "Beispiel ARCHIVE GmbH: „Zolltarifnummer für Drittlandversand“ auf „nur Hinweis“. Achtung: Ohne Zolldaten kann die Ausfuhr später beim Versand Probleme machen – Verantwortung beim Kunden.",
    customerVisible: true,
    to: "/settings/validation",
  },
  {
    id: "2026-10-07-product-rules",
    date: "2026-10-07",
    kind: "new",
    area: "Artikel",
    title: "Artikelregeln",
    summary: "Stammdaten automatisch ergänzen: WENN z. B. der Name „T-Shirt“ enthält, DANN Gewicht 0,2 kg und HS-Code setzen. Gilt bei jeder Neuanlage und jedem Abgleich aus Shop, Odoo oder Lager.",
    details: [
      "Bedingungen: Name, SKU, Hersteller, Shopify-Produkttyp und -Tags, Feld leer/gefüllt",
      "Aktionen: Gewicht, Maße, HS-Code, Ursprungsland, Zollbeschreibung, Zollwert, zerbrechlich – „nur wenn leer“ oder „immer“",
      "Vorschau vor dem Speichern, Versionen, „Auf bestehende Artikel anwenden“",
      "Von Hand gepflegte Felder bleiben unberührt; jede Änderung im Verlauf mit Regelname; geänderte Artikel gehen neu ans Lager",
    ],
    internal: "Einstellungen → Lager & Bestand → Artikelregeln (Recht „Artikel“). Shopify-Produkttyp/Tags werden ab jetzt beim Import gespeichert (nur für Regeln, gehen nicht ans FFN) – für Bestandsartikel erst nach dem nächsten Abgleich vorhanden.",
    customerVisible: true,
    to: "/rules/products",
  },
  {
    id: "2026-10-07-weight-lock-icon",
    date: "2026-10-07",
    kind: "fixed",
    area: "Artikel",
    title: "Schloss am Gewicht wieder korrekt",
    summary: "Nach einer Gewichtsänderung im Portal zeigt das Schloss am Gewicht wieder „geschützt“ an.",
    internal: "Seit dem Gewichts-Fix am Vormittag wurde der Schutz unter „Netto“ abgelegt (wirkte, war aber am Feld nicht sichtbar). Betroffene Artikel einmalig korrigiert.",
    customerVisible: true,
  },
  {
    id: "2026-10-07-shopify-shared-budget",
    date: "2026-10-07",
    kind: "fixed",
    area: "Shopify",
    title: "Keine „Throttled“-Störungen mehr bei großen Shops",
    summary: "Laufen bei einem Shop viele Abgleiche gleichzeitig (Import, Standort-Aktivierung, Auftragsabruf), teilen sie sich jetzt das Shopify-Abfragekontingent und warten vorher, statt nach wenigen Versuchen aufzugeben.",
    details: ["Auftragsabruf und Versandmeldungen haben Vorrang: Import und Standort-Aktivierung lassen 40 % des Kontingents frei", "Je Shop nur ein Aktivierungslauf gleichzeitig", "Gedrosselte Artikel bleiben „ausstehend“ und kommen im nächsten Lauf dran (nicht erst nach 24 Stunden)"],
    internal: "Anlass 7.10.: ARCHIVE GmbH – Standort-Aktivierung eines großen Katalogs plus viele Produkt-Webhooks (jeder stieß einen eigenen Aktivierungslauf an); der Auftragsabruf scheiterte zeitweise mit „Throttled“. Kontingent gilt je Worker-Prozess; mehr Versuche (8) mit wachsender Pause.",
    customerVisible: false,
  },
  {
    id: "2026-10-07-shopify-push-stock-off",
    date: "2026-10-07",
    kind: "new",
    area: "Shopify",
    title: "Bestandsmeldung an Shopify ausschaltbar",
    summary: "Je Shop unter Synchronisation → Bestand: „Wir pflegen den Bestand in Shopify“. Connect ändert dann keine Bestände mehr in Shopify – weder laufend, noch per „Alle Bestände übertragen“, noch beim Aktivieren neuer Artikel am Logiful-Standort.",
    details: ["Shopifys stündliche Bestandsabfrage (fetch_stock) bekommt dann eine leere Antwort – Shopify behält die selbst gepflegten Bestände", "Aufträge kommen weiter bei Logiful an", "Wieder einschalten überträgt einmal alle Bestände"],
    internal: "Risiko für den Kunden: Überverkauf, wenn der Shopify-Bestand nicht zum Lager passt. Umschalten steht im Verlauf des Shops.",
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-07-location-on-release",
    date: "2026-10-07",
    kind: "fixed",
    area: "Shopify",
    title: "„Nur freigegebene Artikel“: Standort sofort nach der Freigabe aktiv",
    summary: "Shops, die nur für Logiful freigegebene Artikel am Logiful-Standort führen, aktivieren neu freigegebene Artikel jetzt sofort – auch bei automatischer Freigabe beim Übertragen und wenn die Freigabe im Lager erkannt wird.",
    internal: "Vorher nur bei Freigabe durch den Kunden im Portal; sonst warteten die Artikel bis zum nächsten Import. Modus „Alle importierten Artikel“ (Standard) aktiviert wie bisher direkt nach dem Import.",
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-07-shipping-profiles-fix",
    date: "2026-10-07",
    kind: "new",
    area: "Shopify",
    title: "Shop-Check: Logiful-Standort per Klick in die Versandprofile",
    summary: "Fehlt der Logiful-Standort in einem Versandprofil (Shopify: „Nicht versendet von“), nimmt „Logiful aufnehmen“ im Shop-Check ihn mit denselben Versandtarifen wie der Händler-Standort auf.",
    details: ["Nur Profile mit Artikeln und das Standardprofil; bereits eingebundene bleiben unverändert", "Jede Aufnahme steht im Verlauf des Shops"],
    internal: "Braucht die Berechtigung write_shipping (empfohlen seit 7.10.). Ohne sie bleibt es beim Hinweis mit Link in den Shopify-Admin. Profile ganz ohne Standortgruppe/Tarife kann nur der Händler einrichten – das meldet der Klick zurück.",
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-07-order-channel-logo",
    date: "2026-10-07",
    kind: "improved",
    area: "Aufträge",
    title: "Kanal-Logo in der Auftragsliste",
    summary: "Vor dem Shopnamen steht das Logo des Kanals (Shopify, Odoo, Amazon, Kaufland …) – auch in der Spalte „Shop“.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-07-shopify-recommended-scopes",
    date: "2026-10-07",
    kind: "improved",
    area: "Shopify",
    title: "Weitere Shopify-Berechtigungen (empfohlen)",
    summary: "Neue Verbindungen fragen zusätzlich vier Berechtigungen an: volle Bestellhistorie, Versandprofile, Bestellungen an anderen Standorten und Schreiben in Bestellungen. Der Shop-Check zeigt fehlende als Hinweis mit Nutzen.",
    details: [
      "read_all_orders – volle Bestellhistorie statt 60 Tage",
      "write_shipping – Versandprofile prüfen, Logiful-Standort per Klick aufnehmen (folgt)",
      "read_merchant_managed_fulfillment_orders – Bestellungen am falschen Standort erkennen (folgt)",
      "write_orders – Hinweise/Tags und korrigierte Adressen in die Bestellung schreiben (folgt)",
    ],
    internal: "Keine Pflicht: bestehende Shops laufen ohne sie unverändert weiter (Shop-Check „Hinweis“, kein Fehler). Für eigene Apps je Kunde: im Dev Dashboard eine neue App-Version mit den Berechtigungen aus der Anleitung (Shops → Shopify verbinden) veröffentlichen, dann im Shopify-Admin bestätigen bzw. neu verbinden. Beim Live-Kunden (u5dtvq-xq) noch offen.",
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-07-shipment-report-race",
    date: "2026-10-07",
    kind: "fixed",
    area: "Versand",
    title: "Keine Fehlalarme mehr bei der Versandmeldung an Shopify",
    summary: "Kam dieselbe Sendung zeitgleich über zwei Wege vom Lager, meldete der zweite einen Fehler („Keine offenen Positionen“ / „api_client does not have access“), obwohl die Sendung längst bei Shopify war. Jetzt meldet je Sendung nur ein Weg.",
    details: ["Ist der Auftrag in Shopify schon vollständig erfüllt (z. B. von Hand), gilt die Sendung als gemeldet – mit Vermerk im Verlauf statt Fehler"],
    internal: "Betraf am 7.10. vier Sendungen (07:58 und 08:38 Uhr) – alle sind korrekt bei Shopify angekommen; die gescheiterten Jobs im Systemstatus können gelöscht werden.",
    customerVisible: false,
  },
  {
    id: "2026-10-07-shopify-line-price-variant",
    date: "2026-10-07",
    kind: "fixed",
    area: "Shopify",
    title: "Shopify-Positionen mit Variantenname und Einzelpreis",
    summary: "Neue Shopify-Aufträge zeigen je Position den Einzelpreis und den Namen mit Variante („Kokoro Elektrische Wärmflasche – Rose Hug“) – auch in der Übergabe ans Lager.",
    details: ["Gilt für Aufträge ab jetzt; bereits übernommene Aufträge behalten ihre Positionen", "Einzelpreis = Listenpreis der Variante vor Rabatten"],
    internal: "Bisher fragte Connect je Position nur SKU, Produktname und Menge ab – deshalb fehlten Preise und die Variante (auch in den FFN-Auslieferungen). Kennt eine Shopify-API-Version die neuen Felder nicht, fragt Connect automatisch ohne sie ab (Warnung im Worker-Log) – die Auftragsübernahme bleibt unberührt.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-07-weight-lock-fix",
    date: "2026-10-07",
    kind: "fixed",
    area: "Artikel",
    title: "Im Portal geändertes Gewicht wurde vom Shopify-Abgleich überschrieben",
    summary: "Ein im Portal gesetztes Gewicht ist jetzt vollständig vor dem Shopify- und Kanal-Abgleich geschützt. Betroffene Artikel wurden einmalig auf den im Portal gesetzten Wert zurückgesetzt und neu ans Lager übertragen.",
    internal: "Ursache: Der Schutz galt nur für das Brutto, der Abgleich schrieb weiter das Netto, und die Regel Brutto = Netto (seit 7.10. nachts) übernahm es ins Brutto. Rücksetzungen stehen im Verlauf des Artikels („Gewicht … zurückgesetzt“).",
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-07-plausibility-confirm",
    date: "2026-10-07",
    kind: "improved",
    area: "Artikel",
    title: "Gewichts-Hinweis mit „Stimmt so“ bestätigen",
    summary: "Passt das Gewicht nicht zur Größe, der Artikel ist aber wirklich so (z. B. Kissen), kann der Hinweis am Artikel mit „Stimmt so“ bestätigt werden.",
    details: ["Die Bestätigung gilt für die gespeicherten Werte – ändern sich Gewicht oder Maße, wird wieder geprüft", "Der Punkt verschwindet aus dem Handlungsbedarf", "Bestätigung steht im Änderungsprotokoll des Artikels"],
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-07-profit-center",
    date: "2026-10-07",
    kind: "new",
    area: "Module",
    title: "Profit Center (Etappe 1)",
    summary: "Deckungsbeitrag je Bestellung, Artikel und Kanal: Umsatz netto − Wareneinsatz − Gebühren − (optional) Fulfillment-Kosten.",
    details: [
      "Einstandspreis je Artikel als gleitender Durchschnitt aus jedem gebuchten Wareneingang; Preis je Anlieferung kommt aus der Bestellung (Einkauf Pro) oder wird eingetragen",
      "Versendete Aufträge behalten den Einstandspreis vom Versandtag",
      "Shopify: Preise je Position, Rabatte, Steuer, Versand und Erstattungen stündlich – getrennt von der Auftragsübernahme",
      "Andere Kanäle: netto mit einstellbarem Steuersatz geschätzt (gekennzeichnet)",
      "Gebühren je Kanal (Prozent + fester Betrag)",
      "Eigenes Recht „Profit Center“: Admins immer, alle anderen standardmäßig kein Zugriff",
    ],
    internal: "Freischalten unter Module & Preise → Profit Center (zwei Pakete: ohne/mit Fulfillment-Kosten aus der Abrechnung). Erster Abgleich holt bis zu 90 Tage (Shopify liefert ohne read_all_orders ~60). Unsere Versand-Einkaufspreise (Versandentscheidung) fließen bewusst NICHT ein. Werbekosten folgen als Etappe 2.",
    customerVisible: true,
    to: "/profit",
  },
  {
    id: "2026-10-07-eu-export",
    date: "2026-10-07",
    kind: "improved",
    area: "Aufträge",
    title: "EU oder Export auf einen Blick",
    summary: "In der Auftragsliste steht am Empfänger die EU-Flagge oder ein Globus (Export, Land im Tooltip); in der Detailansicht die Länderflagge, bei Drittländern zusätzlich „Export“.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-07-weight-checks",
    date: "2026-10-07",
    kind: "new",
    area: "Artikel",
    title: "Gewicht prüfen: unpassend zur Größe und starke Änderungen",
    summary: "Neue Prüfregel „Gewicht passt nicht zur Größe“ (Dichte < 0,02 oder > 3 kg/l) und Hinweis bei Gewichtsänderung um mehr als 50 % – am Artikel und im Handlungsbedarf.",
    details: ["Starke Änderung über jeden Weg (Portal, Shopify, Odoo, Import) mit „Gewicht stimmt“ bestätigen", "Neue Prüfregeln prüfen automatisch alle Artikel neu"],
    internal: "Hintergrund (nie an Kunden): Pakete werden im Warenausgang nicht gewogen – falsches Gewicht führt zu Nachberechnungen der Carrier zu Lasten von Logiful.",
    customerVisible: true,
    to: "/products",
  },
  {
    id: "2026-10-07-weight-unified",
    date: "2026-10-07",
    kind: "improved",
    area: "Artikel",
    title: "Ein Feld „Gewicht“ – Brutto und Netto immer gleich",
    summary: "Im Portal gibt es nur noch „Gewicht“ (inklusive Verpackung). Die Datenbank hält Brutto und Netto auf allen Wegen gleich, das Lager bekommt beide gleich.",
    details: ["Einmalige Angleichung: abweichende Artikel wurden angeglichen und neu ans Lager übertragen"],
    internal: "Grund: unterschiedliche Werte ließen die DHL-Labelerstellung scheitern. Gegenüber Kunden nicht kommentieren.",
    customerVisible: true,
  },
  {
    id: "2026-10-06-export-document",
    date: "2026-10-06",
    kind: "new",
    area: "Aufträge",
    title: "Ausfuhrbegleitdokument (ABD)",
    summary: "Drittland-Aufträge ab 1.000 € Warenwert oder 1.000 kg werden zurückgehalten, bis das PDF im Auftrag hochgeladen und freigegeben ist; danach geht es als Anhang ans Lager.",
    details: ["Grenzen je Kunde einstellbar", "Handlungsbedarf und Filter „Rückhaltegrund“"],
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-06-order-columns",
    date: "2026-10-06",
    kind: "new",
    area: "Aufträge",
    title: "Spaltenauswahl und Sammelaktionen in der Auftragsliste",
    summary: "Spalten frei wählbar (Land, Shop, Versandart, Zahlstatus, Gewicht, Tags, Lager-Nr., Sendungsnummer …); mehrere Aufträge auf einmal bearbeiten mit Ergebnis je Auftrag und CSV-Export.",
    customerVisible: true,
    to: "/orders",
  },
  {
    id: "2026-10-06-delivery-mails",
    date: "2026-10-06",
    kind: "new",
    area: "Shopify",
    title: "Versand- und Zustell-Mails je Shop: Shopify, Klaviyo oder keine",
    summary: "Je Shopify-Shop wählbar, wer Endkunden über Versand und Zustellung informiert. Bei „Shopify“ melden wir die Zustellstufen als Fulfillment-Events.",
    internal: "Umstellen nur durch Logiful-Admins (Kanal-Einstellungen). Standard: keine.",
    customerVisible: true,
  },
  {
    id: "2026-10-06-shop-check",
    date: "2026-10-06",
    kind: "new",
    area: "Shopify",
    title: "Shop-Check mit Ein-Klick-Lösungen",
    summary: "Prüft live Standort, Versandprofile, Berechtigungen, Kundendaten-Freigabe, Webhooks, Fulfillment-Dienst, Artikel am Standort, abgelehnte Anforderungen und API-Version.",
    details: ["Bestandsabfrage des Logiful-Standorts (fetch_stock) wird beantwortet statt stündlicher 404"],
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-06-address-housenumber",
    date: "2026-10-06",
    kind: "improved",
    area: "Adressen",
    title: "Hausnummer aus Adresszeile 2 und lückenloses Korrektur-Protokoll",
    summary: "Steht die Hausnummer im Adresszusatz, wird sie vor der Übergabe in die Straße verschoben (DE/AT/CH/NL/BE/LU). Jede Adressänderung steht mit Vorher/Nachher im Verlauf und als Schritt im Auftrag.",
    internal: "Logiful-Liste der betroffenen Aufträge beim Lager: Logiful intern → Adressprüfung → „Hausnummer im Zusatz“.",
    customerVisible: true,
  },
  {
    id: "2026-10-06-i18n",
    date: "2026-10-06",
    kind: "new",
    area: "Portal",
    title: "Englische Oberfläche (Fundament)",
    summary: "Sprachwahl je Nutzer (Deutsch/English) im Benutzermenü; neue Texte kommen immer zweisprachig.",
    customerVisible: true,
  },
  {
    id: "2026-10-06-shopify-fixes",
    date: "2026-10-06",
    kind: "fixed",
    area: "Shopify",
    title: "Shopify: Rücksprung-Domain, doppelte Meldungen, Fulfillment-Anfragen",
    summary: "Verbinden klappt über app.logiful.de; gleichzeitige Meldungen zum selben Fulfillment-Auftrag legen ihn genau einmal an; Fulfillment- und Storno-Anfragen lösen wieder sofort den Abgleich aus.",
    customerVisible: false,
  },
  {
    id: "2026-10-05-odoo",
    date: "2026-10-05",
    kind: "new",
    area: "Kanäle",
    title: "Odoo-Anbindung",
    summary: "Aufträge, Versandmeldung, eigene Felder, Artikel und Bestand mit Chargen/MHD; Versandmethoden aus Odoo für die Versandarten-Zuordnung.",
    details: ["Artikelübernahme standardmäßig aus (nur auf Wunsch)", "Auftragsnummer je Kanal wählbar (Lieferschein oder Verkaufsauftrag)", "Unternehmen einstellbar"],
    customerVisible: true,
    to: "/channels",
  },
  {
    id: "2026-10-05-refetch",
    date: "2026-10-05",
    kind: "new",
    area: "Aufträge",
    title: "Aufträge gezielt abrufen und neu abrufen",
    summary: "Aufträge nach Nummer oder Zeitraum mit Vorschau abrufen (alle Kanäle); einen Auftrag vor Übergabe ans Lager neu abrufen (Adresse, Positionen, Chargen) – korrigierte Adresse bleibt.",
    customerVisible: true,
  },
  {
    id: "2026-10-05-pack-units",
    date: "2026-10-05",
    kind: "new",
    area: "Artikel",
    title: "Gebinde je Artikel",
    summary: "Verpackungseinheiten mit eigener EAN – pflegbar, ans Lager (FFN bundles), aus Odoo-Verpackungen, B2B in ganzen Kartons.",
    customerVisible: true,
  },
  {
    id: "2026-10-05-shopify-paid",
    date: "2026-10-05",
    kind: "improved",
    area: "Shopify",
    title: "Zahlstatus aktuell",
    summary: "Zahlstatus per Webhook „orders/paid“ und stündlichem Abgleich; Webhooks werden täglich sichergestellt.",
    customerVisible: false,
  },
  {
    id: "2026-10-05-devtools",
    date: "2026-10-05",
    kind: "new",
    area: "Logiful intern",
    title: "Werkzeugkasten für Logiful-Admins",
    summary: "Versand, Zustellstatus, Retoure, Wareneingang und Bestand simulieren, Abgleich sofort auslösen – mit Rückfrage bei echten Kunden.",
    internal: "Schraubenschlüssel unten rechts. Simulierte Daten sind gekennzeichnet und lassen sich zurücksetzen.",
    customerVisible: false,
  },
  {
    id: "2026-10-05-klaviyo",
    date: "2026-10-05",
    kind: "new",
    area: "Module",
    title: "Tool-Anbindungen: Klaviyo",
    summary: "Versand-, Zustell-, Retouren- und Verzögerungsereignisse an Klaviyo für eigene Mails im eigenen Design.",
    internal: "Modul „Tool-Anbindungen“ – Freischaltung durch Logiful.",
    customerVisible: true,
  },
  {
    id: "2026-10-04-track-trace",
    date: "2026-10-04",
    kind: "new",
    area: "Versand",
    title: "Track & Trace und Versandkonten",
    summary: "Zustellstatus je Sendung (DHL Parcel DE), eigene Seite mit Lagebild und Laufzeiten; Zustellprobleme im Handlungsbedarf.",
    internal: "DHL-Konto hängt an der Versandart (Versandkonten unter Logiful intern).",
    customerVisible: true,
    to: "/tracking",
  },
  {
    id: "2026-10-04-modules",
    date: "2026-10-04",
    kind: "new",
    area: "Module",
    title: "Modulsystem mit Paketen",
    summary: "Kunden buchen, testen und kündigen Module selbst; Logiful verwaltet Pakete, Preise, Testphasen, Sonderpreise und Bündel.",
    details: ["Module u. a.: Adressprüfung, Retourenportal, Kanäle, Shop-Widgets, Auswertungen, Kundenkommunikation, Einkauf Pro, B2B, Pre-FBA, KI-Assistent, Schnittstelle"],
    customerVisible: true,
    to: "/settings/modules",
  },
  {
    id: "2026-10-04-b2b-fba",
    date: "2026-10-04",
    kind: "new",
    area: "Aufträge",
    title: "B2B-Lieferungen und Pre-FBA",
    summary: "Lieferungen an Händler mit Vorgaben und Lieferschein; Sendungen an Amazon-Lager mit FNSKU und Etiketten.",
    customerVisible: true,
  },
  {
    id: "2026-10-04-team-security",
    date: "2026-10-04",
    kind: "new",
    area: "Konto",
    title: "Rechte je Bereich, Zwei-Faktor-Anmeldung, Änderungsprotokoll",
    summary: "Mitarbeiter-Rechte je Bereich (bearbeiten/ansehen/kein Zugriff), 2FA (auch als Pflicht fürs Team) und ein Protokoll aller Änderungen.",
    customerVisible: true,
  },
  {
    id: "2026-10-04-monitoring",
    date: "2026-10-04",
    kind: "new",
    area: "Logiful intern",
    title: "Systemstatus mit Alarmen und Staging-Umgebung",
    summary: "Störungen (Warteschlangen, Abgleiche, Jobs) mit Entwarnung unter Systemstatus; eigene Staging-Umgebung nur mit FFN-Sandbox.",
    customerVisible: false,
    to: "/admin/system",
  },
  {
    id: "2026-10-03-test-mode",
    date: "2026-10-03",
    kind: "new",
    area: "Logiful intern",
    title: "Testbetrieb je Kunde",
    summary: "Nichts geht ans Lager, keine Bestände/Standort an Shops – Auftragseingang, Regeln und Retouren gefahrlos prüfen; neue Shopify-Bestellungen nur lesend als Testaufträge.",
    customerVisible: false,
  },
  {
    id: "2026-10-03-returns-shopify",
    date: "2026-10-03",
    kind: "new",
    area: "Retouren",
    title: "Retouren an Shopify melden",
    summary: "Je Kunde: über Connect, über die eigene App oder aus; Erstattung optional. Klassifizierung A/B/C überall sichtbar.",
    customerVisible: true,
    to: "/returns",
  },
];

export const CHANGE_KIND_LABEL: Record<ChangeKind, string> = { new: msg("Neu"), improved: msg("Verbessert"), fixed: msg("Behoben") };

const SEEN_KEY = "lc-changelog-seen";

/** gelesene Einträge (je Browser) – nur Komfort für „neu seit dem letzten Besuch“ */
export function readSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
export function markAllSeen() {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(CHANGELOG.map((e) => e.id)));
    window.dispatchEvent(new Event("lc-changelog-seen"));
  } catch {
    /* ohne Speicher: kein Merken */
  }
}

/** Zahl ungelesener Einträge für den Menüpunkt */
export function useUnseenChangelog(): number {
  const [n, setN] = useState(() => CHANGELOG.filter((e) => !readSeen().has(e.id)).length);
  useEffect(() => {
    const update = () => setN(CHANGELOG.filter((e) => !readSeen().has(e.id)).length);
    window.addEventListener("lc-changelog-seen", update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener("lc-changelog-seen", update); window.removeEventListener("storage", update); };
  }, []);
  return n;
}

